import React, { memo, useId, forwardRef, ReactNode } from 'react';
import { motion, LazyMotion, domAnimation, AnimatePresence } from 'framer-motion';

// Type definitions
interface IconContainerProps {
  children: ReactNode;
  variant: 'left' | 'center' | 'right';
  className?: string;
  theme?: 'light' | 'dark' | 'neutral';
}

interface MultiIconDisplayProps {
  icons: ReactNode[];
  theme?: 'light' | 'dark' | 'neutral';
}

interface BackgroundProps {
  theme?: 'light' | 'dark' | 'neutral';
}

interface ActionProps {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  disabled?: boolean;
}

interface QuotaInfo {
  cost: number;
  remaining: number | null;
  costLabel?: string;
  remainingLabel?: string;
  unitLabel?: string;
  insufficient: boolean;
  /** When true, show loading state instead of cost value */
  isLoading?: boolean;
}

interface EmptyStateProps {
  title: string;
  description?: string;
  icons?: ReactNode[];
  action?: ActionProps;
  variant?: 'default' | 'subtle' | 'error';
  size?: 'sm' | 'default' | 'lg';
  theme?: 'light' | 'dark' | 'neutral';
  isIconAnimated?: boolean;
  className?: string;
  usageFeedback?: UsageFeedbackDetails | null;
  quotaInfo?: QuotaInfo | null;
  [key: string]: any;
}

interface UsageFeedbackDetails {
  key: number;
  previous: number;
  current: number;
  limit?: number;
  headline: string;
  subline: string;
}

const ICON_VARIANTS = {
  left: {
    initial: { scale: 0.8, opacity: 0, x: 0, y: 0, rotate: 0 },
    animate: { scale: 1, opacity: 1, x: 0, y: 0, rotate: -6, transition: { duration: 0.4, delay: 0.1 } },
    hover: { x: -22, y: -5, rotate: -15, scale: 1.1, transition: { duration: 0.2 } }
  },
  center: {
    initial: { scale: 0.8, opacity: 0 },
    animate: { scale: 1, opacity: 1, transition: { duration: 0.4, delay: 0.2 } },
    hover: { y: -10, scale: 1.15, transition: { duration: 0.2 } }
  },
  right: {
    initial: { scale: 0.8, opacity: 0, x: 0, y: 0, rotate: 0 },
    animate: { scale: 1, opacity: 1, x: 0, y: 0, rotate: 6, transition: { duration: 0.4, delay: 0.3 } },
    hover: { x: 22, y: -5, rotate: 15, scale: 1.1, transition: { duration: 0.2 } }
  }
};

const CONTENT_VARIANTS = {
  initial: { y: 20, opacity: 0 },
  animate: { y: 0, opacity: 1, transition: { duration: 0.4, delay: 0.2 } },
};

const BUTTON_VARIANTS = {
  initial: { y: 20, opacity: 0 },
  animate: { y: 0, opacity: 1, transition: { duration: 0.4, delay: 0.3 } },
};

export const cn = (...classes: (string | boolean | undefined | null)[]): string => classes.filter(Boolean).join(' ');

const IconContainer = memo<IconContainerProps>(({ children, variant, className = '', theme }) => (
  <motion.div
    variants={ICON_VARIANTS[variant]}
    className={cn(
      "w-12 h-12 rounded-xl flex items-center justify-center relative shadow-lg transition-all duration-300",
      theme === 'dark' && "bg-neutral-800 border border-neutral-700 group-hover:shadow-xl group-hover:border-neutral-600",
      theme === 'neutral' && "bg-stone-100 border border-stone-200 group-hover:shadow-xl group-hover:border-stone-300",
      (!theme || theme === 'light') && "bg-white border border-gray-200 group-hover:shadow-xl group-hover:border-gray-300",
      className
    )}
  >
    <div className={cn(
      "text-sm transition-colors duration-300",
      theme === 'dark' && "text-neutral-400 group-hover:text-neutral-200",
      theme === 'neutral' && "text-stone-500 group-hover:text-stone-700",
      (!theme || theme === 'light') && "text-gray-500 group-hover:text-gray-700"
    )}>
      {children}
    </div>
  </motion.div>
));
IconContainer.displayName = "IconContainer";

const MultiIconDisplay = memo<MultiIconDisplayProps>(({ icons, theme }) => {
  if (!icons || icons.length < 3) return null;

  return (
    <div className="flex justify-center isolate relative">
      <IconContainer variant="left" className="left-2 top-1 z-10" theme={theme}>
        {icons[0]}
      </IconContainer>
      <IconContainer variant="center" className="z-20" theme={theme}>
        {icons[1]}
      </IconContainer>
      <IconContainer variant="right" className="right-2 top-1 z-10" theme={theme}>
        {icons[2]}
      </IconContainer>
    </div>
  );
});
MultiIconDisplay.displayName = "MultiIconDisplay";

const Background: React.FC<BackgroundProps> = ({ theme }) => (
  <div
    aria-hidden="true"
    className="absolute inset-0 opacity-0 group-hover:opacity-[0.02] transition-opacity duration-500"
    style={{
      backgroundImage: `radial-gradient(circle at 2px 2px, #fff 1px, transparent 1px)`,
      backgroundSize: '24px 24px'
    }}
  />
);

export const EmptyState = forwardRef<HTMLElement, EmptyStateProps>(({
  title,
  description,
  icons,
  action,
  variant = 'default',
  size = 'default',
  theme = 'light',
  isIconAnimated = true,
  className = '',
  usageFeedback,
  quotaInfo,
  ...props
}, ref) => {
  const titleId = useId();
  const descriptionId = useId();

  const baseClasses = "group transition-all duration-300 rounded-xl relative overflow-hidden text-center flex flex-col items-center justify-center";

  const sizeClasses = {
    sm: "p-6",
    default: "p-8",
    lg: "p-12"
  };

  const getVariantClasses = (variant: string, theme?: string) => {
    const variants = {
      default: {
        light: "",
        dark: "",
        neutral: "bg-stone-50 border-dashed border-2 border-stone-300 hover:border-stone-400 hover:bg-stone-100/50"
      },
      subtle: {
        light: "bg-white hover:bg-gray-50/30",
        dark: "bg-neutral-900 border border-transparent hover:bg-neutral-800/30",
        neutral: "bg-stone-50 border border-transparent hover:bg-stone-100/30"
      },
      error: {
        light: "bg-white  bg-red-50/50 hover:bg-red-50/80",
        dark: "bg-neutral-900 border border-red-800 bg-red-950/50 hover:bg-red-950/80",
        neutral: "bg-stone-50 border border-red-300 bg-red-50/50 hover:bg-red-50/80"
      }
    };
    return variants[variant as keyof typeof variants]?.[theme as keyof typeof variants.default] || variants.default.light;
  };

  const getTextClasses = (type: 'title' | 'description', size: string, theme?: string) => {
    const sizes = {
      title: {
        sm: "text-base",
        default: "text-lg",
        lg: "text-xl"
      },
      description: {
        sm: "text-xs",
        default: "text-sm",
        lg: "text-base"
      }
    };

    const colors = {
      title: {
        light: "text-gray-900",
        dark: "text-neutral-100",
        neutral: "text-stone-900"
      },
      description: {
        light: "text-gray-600",
        dark: "text-neutral-400",
        neutral: "text-stone-600"
      }
    };

    return cn(sizes[type][size as keyof typeof sizes.title], colors[type][theme as keyof typeof colors.title], "font-semibold transition-colors duration-200");
  };

  const getButtonClasses = (size: string, theme?: string) => {
    const sizeClasses = {
      sm: "text-xs px-3 py-1.5",
      default: "text-sm px-4 py-2",
      lg: "text-base px-6 py-3"
    };

    const themeClasses = {
      light: "border-gray-300 bg-white hover:bg-gray-50 text-gray-700",
      dark: "border-neutral-600 bg-neutral-800 hover:bg-neutral-700 text-neutral-200",
      neutral: "border-stone-300 bg-stone-100 hover:bg-stone-200 text-stone-700"
    };

    return cn(
      "inline-flex items-center gap-2 border rounded-md font-medium shadow-sm hover:shadow-md transition-all duration-200 relative overflow-hidden group/button disabled:opacity-50 disabled:cursor-not-allowed",
      sizeClasses[size as keyof typeof sizeClasses],
      themeClasses[theme as keyof typeof themeClasses]
    );
  };

  const secondaryTextClass = theme === 'dark'
    ? 'text-neutral-400'
    : theme === 'neutral'
      ? 'text-stone-500'
      : 'text-gray-500';

  const accentTextClass = theme === 'dark'
    ? 'text-emerald-400'
    : 'text-emerald-600';

  const progressBackgroundClass = theme === 'dark'
    ? 'bg-neutral-700/70'
    : theme === 'neutral'
      ? 'bg-stone-200/80'
      : 'bg-gray-200/80';

  const progressFillClass = theme === 'dark'
    ? 'bg-emerald-400/80'
    : 'bg-emerald-500';

  return (
    <LazyMotion features={domAnimation}>
      <motion.section
        ref={ref}
        role="region"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className={cn(
          baseClasses,
          sizeClasses[size as keyof typeof sizeClasses],
          getVariantClasses(variant, theme),
          className
        )}
        initial="initial"
        animate="animate"
        whileHover={isIconAnimated ? "hover" : "animate"}
        {...props}
      >
        <Background theme={theme} />
        <div className="relative z-10 flex flex-col items-center">
          {icons && (
            <div className="mb-6">
              <MultiIconDisplay icons={icons} theme={theme} />
            </div>
          )}

          <motion.div variants={CONTENT_VARIANTS} className="space-y-2 mb-6">
            <h2 id={titleId} className={getTextClasses('title', size, theme)}>
              {title}
            </h2>
            {description && (
              <p
                id={descriptionId}
                className={cn(
                  getTextClasses('description', size, theme).replace('font-semibold', ''),
                  "max-w-md leading-relaxed"
                )}
              >
                {description}
              </p>
            )}
          </motion.div>

          {/* Quota Info Display */}
          {quotaInfo && (
            <motion.div 
              variants={CONTENT_VARIANTS}
              className="mb-5 flex flex-col items-center gap-3"
            >
              <div className={cn(
                "inline-flex items-center gap-1 px-1 py-1 rounded-full text-xs",
                theme === 'dark' 
                  ? "bg-neutral-800/40 backdrop-blur-sm" 
                  : theme === 'neutral'
                    ? "bg-stone-100/60 backdrop-blur-sm"
                    : "bg-white/60 backdrop-blur-sm shadow-sm"
              )}>
                {/* Cost Pill */}
                <div className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors",
                  theme === 'dark' 
                    ? "bg-neutral-700/60" 
                    : theme === 'neutral'
                      ? "bg-stone-200/80"
                      : "bg-gray-100/80"
                )}>
                  <svg 
                    className={cn(
                      "w-3.5 h-3.5",
                      theme === 'dark' ? "text-amber-400" : "text-amber-500",
                      quotaInfo.isLoading && "animate-pulse"
                    )} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {quotaInfo.isLoading ? (
                    <span className={cn(
                      "w-6 h-4 rounded animate-pulse",
                      theme === 'dark' ? "bg-neutral-600" : "bg-gray-200"
                    )} />
                  ) : (
                    <span className={cn(
                      "font-medium tabular-nums",
                      theme === 'dark' ? "text-neutral-200" : "text-gray-700"
                    )}>
                      {quotaInfo.cost}
                    </span>
                  )}
                  <span className={cn(
                    "text-[10px] uppercase tracking-wide",
                    theme === 'dark' ? "text-neutral-400" : "text-gray-500"
                  )}>
                    {quotaInfo.unitLabel || 'min'}
                  </span>
                </div>
                
                {/* Arrow */}
                <svg 
                  className={cn(
                    "w-4 h-4 mx-0.5",
                    theme === 'dark' ? "text-neutral-500" : "text-gray-400"
                  )} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                
                {/* Remaining Pill */}
                <div className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all",
                  quotaInfo.insufficient 
                    ? "bg-red-500/10 ring-1 ring-red-500/20" 
                    : theme === 'dark' 
                      ? "bg-emerald-500/10" 
                      : theme === 'neutral'
                        ? "bg-emerald-50"
                        : "bg-emerald-50/80"
                )}>
                  <svg 
                    className={cn(
                      "w-3.5 h-3.5",
                      quotaInfo.insufficient 
                        ? "text-red-500" 
                        : theme === 'dark' ? "text-emerald-400" : "text-emerald-500"
                    )} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  <span className={cn(
                    "font-semibold tabular-nums",
                    quotaInfo.insufficient 
                      ? "text-red-600" 
                      : theme === 'dark' ? "text-emerald-400" : "text-emerald-600"
                  )}>
                    {quotaInfo.remaining !== null ? quotaInfo.remaining : '∞'}
                  </span>
                  <span className={cn(
                    "text-[10px] uppercase tracking-wide",
                    quotaInfo.insufficient 
                      ? "text-red-500/70" 
                      : theme === 'dark' ? "text-emerald-400/70" : "text-emerald-600/70"
                  )}>
                    {quotaInfo.unitLabel || 'min'}
                  </span>
                </div>
              </div>
              
              {/* Insufficient warning */}
              {quotaInfo.insufficient && (
                <motion.div 
                  initial={{ opacity: 0, y: -4, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium",
                    theme === 'dark' 
                      ? "bg-red-500/10 text-red-400 border border-red-500/20" 
                      : "bg-red-50 text-red-600 border border-red-200/50"
                  )}
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>Insufficient quota</span>
                </motion.div>
              )}
            </motion.div>
          )}

          {action && (
            <motion.div variants={BUTTON_VARIANTS} className="flex flex-col items-center">
              <motion.button
                type="button"
                onClick={action.onClick}
                disabled={action.disabled}
                className={cn(getButtonClasses(size, theme), "rounded-xl")}
                whileTap={{ scale: 0.98 }}
              >
                <AnimatePresence>
                  {usageFeedback && usageFeedback.previous !== usageFeedback.current && (
                    <motion.span
                      key={`usage-flare-${usageFeedback.key}`}
                      className="pointer-events-none absolute inset-0 rounded-xl bg-emerald-500/10"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 1.1 }}
                      transition={{ duration: 0.4, ease: 'easeOut' }}
                    />
                  )}
                </AnimatePresence>
                {action.icon && (
                  <motion.div
                    className="transition-transform group-hover/button:rotate-45"
                    whileHover={{ rotate: 90 }}
                  >
                    {action.icon}
                  </motion.div>
                )}
                <span className="relative z-10">{action.label}</span>
                <AnimatePresence>
                  {usageFeedback && usageFeedback.previous !== usageFeedback.current && (
                    <motion.span
                      key={`usage-delta-${usageFeedback.key}`}
                      className={cn('pointer-events-none absolute -top-3 right-3 text-xs font-semibold drop-shadow', accentTextClass)}
                      initial={{ opacity: 0, y: 4, scale: 0.8 }}
                      animate={{ opacity: 1, y: -6, scale: 1 }}
                      exit={{ opacity: 0, y: -18, scale: 0.9 }}
                      transition={{ duration: 0.45 }}
                    >
                      −1
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.button>
              {usageFeedback && (
                <div className="mt-4 flex flex-col items-center space-y-2 text-center">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={`usage-headline-${usageFeedback.key}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.3 }}
                      className={cn('text-sm font-semibold', accentTextClass)}
                    >
                      {usageFeedback.headline}
                    </motion.span>
                  </AnimatePresence>

                  <AnimatePresence mode="wait">
                    <motion.span
                      key={`usage-subline-${usageFeedback.key}-${usageFeedback.subline}`}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.3, delay: 0.05 }}
                      className={cn('text-xs', secondaryTextClass)}
                    >
                      {usageFeedback.subline}
                    </motion.span>
                  </AnimatePresence>

                  {typeof usageFeedback.limit === 'number' && usageFeedback.limit > 0 && (
                    <div className="flex w-32 flex-col items-center space-y-1">
                      <div className={cn('flex items-baseline gap-1 text-xs font-medium', secondaryTextClass)}>
                        <AnimatePresence mode="wait">
                          <motion.span
                            key={`usage-count-${usageFeedback.key}-${usageFeedback.current}`}
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            transition={{ duration: 0.25 }}
                            className={cn('text-sm font-semibold', theme === 'dark' ? 'text-neutral-100' : 'text-gray-900')}
                          >
                            {usageFeedback.current}
                          </motion.span>
                        </AnimatePresence>
                        <span
                          className={cn(
                            'text-[11px] font-normal',
                            theme === 'dark' ? 'text-neutral-500' : theme === 'neutral' ? 'text-stone-400' : 'text-gray-400'
                          )}
                        >
                          /
                        </span>
                        <span>{usageFeedback.limit}</span>
                      </div>
                      <div className={cn('h-1.5 w-full overflow-hidden rounded-full', progressBackgroundClass)}>
                        <motion.div
                          key={`usage-progress-${usageFeedback.key}-${usageFeedback.current}`}
                          initial={{ width: `${Math.max(0, Math.min(usageFeedback.previous, usageFeedback.limit)) / usageFeedback.limit * 100}%` }}
                          animate={{ width: `${Math.max(0, Math.min(usageFeedback.current, usageFeedback.limit)) / usageFeedback.limit * 100}%` }}
                          transition={{ duration: 0.45, ease: 'easeOut' }}
                          className={cn('h-full', progressFillClass)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </div>
      </motion.section>
    </LazyMotion>
  );
});
EmptyState.displayName = "EmptyState";
